<template>
  <div class="control-level-slider">
    <span class="level-value">
      <input
        ref="manualInput"
        type="text"
        inputmode="numeric"
        :aria-label="label + '数值'"
        :min="0"
        :max="max"
        :value="editText"
        :style="{ width: Math.max(1, (editText || String(editOriginalValue)).length) + 'ch' }"
        @focus="manualFocus"
        @input="manualInput"
        @blur="finishManualEditing(true)"
        @keydown.enter.prevent="confirmManualInput"
        @keydown.esc.prevent="cancelManualInput"
      />
    </span>
    <span class="level-track" :style="{ '--level-color': color }">
      <span
        class="level-fill"
        :style="{ height: Math.max(0, Math.min(100, (value / max) * 100)) + '%' }"
      ></span>
      <input
        type="range"
        :aria-label="label"
        min="0"
        :max="max"
        step="1"
        :value="value"
        @pointerdown="startDrag"
        @pointerup="endDrag"
        @pointercancel="endDrag"
        @input="preview($event.target.value)"
        @change="commit($event.target.value)"
      />
    </span>
    <span class="level-label">{{ label }}</span>
  </div>
</template>
<script>
export default {
  props: {
    value: { type: Number, default: 0 },
    max: { type: Number, default: 255 },
    label: String,
    color: { type: String, default: '#d5d2c8' }
  },
  data() {
    return { editing: false, editText: String(this.value), editOriginalValue: this.value }
  },
  watch: {
    value(value) {
      if (!this.editing) this.editText = String(this.normalize(value))
    }
  },
  mounted() {
    // Capture before controls that prevent pointer defaults or stop propagation.
    document.addEventListener('pointerdown', this.handleOutsidePointerDown, true)
  },
  beforeDestroy() {
    document.removeEventListener('pointerdown', this.handleOutsidePointerDown, true)
    if (this.editing) this.endDrag()
  },
  methods: {
    handleOutsidePointerDown(event) {
      if (!this.editing || event.target === this.$refs.manualInput) return
      this.confirmManualInput()
    },
    manualFocus() {
      this.editing = true
      this.editOriginalValue = this.normalize(this.value)
      this.editText = ''
      this.$emit('drag', true)
    },
    manualInput(event) {
      const source = event.target.value
      this.editText = source
      const trimmed = source.trim()
      if (/^-?(?:\d+|\d*\.\d+)$/.test(trimmed) && Number.isFinite(Number(trimmed))) {
        const value = Number(trimmed)
        if (value < 0) this.editText = '0'
        else if (value > this.max) this.editText = String(this.max)
      }
      // Vue may not patch an unchanged bound value; correct the visible text too.
      if (event.target.value !== this.editText) event.target.value = this.editText
    },
    finishManualEditing(commit) {
      if (!this.editing) return
      const text = this.editText.trim()
      const value = Number(text)
      const valid = commit && /^-?(?:\d+|\d*\.\d+)$/.test(text) &&
        Number.isFinite(value) && value >= 0 && value <= this.max && Number.isInteger(value)
      this.editing = false
      const next = valid ? value : this.editOriginalValue
      this.editText = String(next)
      this.endDrag()
      if (valid && next !== this.editOriginalValue) {
        this.$emit('input', next)
        this.$emit('change', next)
      }
    },
    confirmManualInput() {
      this.finishManualEditing(true)
      this.$refs.manualInput.blur()
    },
    cancelManualInput() {
      this.finishManualEditing(false)
      this.$refs.manualInput.blur()
    },
    normalize(value) {
      return Math.max(0, Math.min(this.max, Math.round(Number(value) || 0)))
    },
    preview(value) {
      this.$emit('input', this.normalize(value))
    },
    commit(value) {
      this.$emit('change', this.normalize(value))
      this.endDrag()
    },
    startDrag(event) {
      event.currentTarget.setPointerCapture(event.pointerId)
      this.$emit('drag', true)
    },
    endDrag() {
      this.$emit('drag', false)
    }
  }
}
</script>
<style scoped>
.control-level-slider {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  color: #d9dce2;
  font-size: 16px;
}
.level-value {
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  line-height: 27px;
}
.level-value input {
  height: 26px;
  min-width: 1ch;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  border: 0;
  background: none;
  padding: 0;
  color: inherit;
  font: inherit;
  text-align: center;
  appearance: textfield;
  -moz-appearance: textfield;
}
.level-value input::-webkit-inner-spin-button,
.level-value input::-webkit-outer-spin-button {
  appearance: none;
  margin: 0;
}
.level-value input:focus {
  outline: 1px solid #8c929d;
  border-radius: 4px;
}
.level-track {
  position: relative;
  display: block;
  width: 48px;
  height: 114px;
  border-radius: 26px;
  background: #555a63;
  overflow: hidden;
  flex: none;
}
.level-fill {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--level-color);
  pointer-events: none;
}
.level-track input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  writing-mode: vertical-lr;
  direction: rtl;
  cursor: pointer;
  touch-action: none;
}
.level-track:focus-within {
  outline: 2px solid #b9c5df;
  outline-offset: 3px;
}
.level-label {
  font-size: 12px;
  color: #adb2bc;
  line-height: 20px;
}
</style>
